import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NavigableRecord, BaseNavigationQueryParams } from './navigable-record.interface';

/**
 * Abstract base for all new master-configuration navigation services.
 *
 * Concrete subclasses only need to declare:
 *   protected readonly basePath: string[]
 *   protected readonly navigationDescription: string
 *
 * Router is injected via inject() so subclasses remain zero-arg @Injectable —
 * no constructor/super boilerplate required.
 */
@Injectable()
export abstract class AbstractNavigationService<
  TData extends NavigableRecord,
  TQuery extends BaseNavigationQueryParams
> {
  protected abstract readonly basePath: string[];
  protected abstract readonly navigationDescription: string;

  protected readonly router = inject(Router);

  /**
   * Navigate to the list screen, optionally with query params.
   * Navigates in the same tab by default (openInNewTab=false).
   * Pass openInNewTab=true to open a new browser tab instead.
   */
  navigateToList(queryParams: TQuery = {} as TQuery, openInNewTab = false): void {
    const tree = this.router.createUrlTree(this.basePath, { queryParams });
    if (openInNewTab) {
      window.open(window.location.origin + this.router.serializeUrl(tree), '_blank');
    } else {
      this.router.navigateByUrl(tree);
    }
  }

  /**
   * Navigate to the list screen pre-filtered to this specific record.
   * Search term is derived from the record via getSearchTerm().
   */
  navigateToDetail(
    data: TData,
    additionalParams: TQuery = {} as TQuery,
    openInNewTab = false
  ): void {
    this.navigateToList(
      { search: this.getSearchTerm(data), ...additionalParams } as TQuery,
      openInNewTab
    );
  }

  /**
   * Build and return the absolute URL for the list screen (does not navigate).
   */
  createUrl(queryParams: TQuery = {} as TQuery): string {
    return (
      window.location.origin +
      this.router.serializeUrl(this.router.createUrlTree(this.basePath, { queryParams }))
    );
  }

  /**
   * Returns true when the record has enough identity information to navigate.
   */
  isNavigable(data: TData | null | undefined): boolean {
    return !!data && !!this.getSearchTerm(data as TData);
  }

  /**
   * Returns the human-readable description declared by the subclass.
   */
  getNavigationDescription(): string {
    return this.navigationDescription;
  }

  /**
   * Priority: externalKeys.code → code → name → ''
   * Matches the getCode pattern used across the codebase.
   */
  protected getSearchTerm(data: TData): string {
    if (!data) return '';
    return data.externalKeys?.code || data.code || data.name || '';
  }
}
